// money.ts — GOLD, the platform's cash currency. $1 = 1 GOLD.
//
// Ported from web-client so the operator console reads money the same way
// users do. On the wire every monetary value is an
// int64 *minor unit* at MINOR_PRECISION = 4 — a "dollar-major" value (10000
// = $1), shown in cents (so "737500" = ⚡7,375). Display truncates to 2 dp.
//
// The unit renders as the ⚡ Spark glyph via <SparkAmount>. For plain text
// (hints, aria-labels, string concatenation) use formatSpark /
// formatSparkText.
//
// Wire note: POST bodies still send the ledger's `currency: "CREDIT"`
// literal and amounts as minor-unit strings — SPARK is a *display* unit,
// not a wire change. parseSparkToMinor converts a typed amount back to the
// minor-unit string the API expects, and moves with the divisor so a form that
// formats and parses through this module still round-trips exactly.
//
// SM-460 retired the legacy SPARK lens (1 SPARK = 1 cent): one displayed whole
// unit of RED, GREEN or GOLD is $1. Nothing on the wire moved — $1.00 is wire
// 10000 as it always was — only how many digits sit left of the decimal point.
// WIRE_PER_UNIT and WIRE_PER_DOLLAR are now the same number, which is the point:
// an amount and a price of the same wire value finally read the same.

export const SPARK_MINOR_PRECISION = 4; // int64 minor-unit precision on the wire
export const SPARK_DISPLAY_DP = 2; // decimals shown to operators
export const SPARK_LABEL = "GOLD";

// One displayed unit is one dollar, and the wire is a dollar-major value at
// precision 4 — so the divisor IS 10^PRECISION. e.g. wire 737500 → 73.75.
const WIRE_PER_UNIT = 10 ** SPARK_MINOR_PRECISION; // 10 000

/**
 * Wire minor units per ONE displayed unit, for SPARK/GOLD. Other currencies
 * carry their own divisor in the SM-443 presentation contract (`unitMinor`) and
 * pass it through FormatSparkOptions — RED and GREEN are shown on the epic's $1
 * currency unit, where 1 unit = wire 10 000 (SM-484).
 *
 * This is a DISPLAY divisor and nothing else (G2). The wire integer is
 * identical for all three currencies — $1.00 is 10000 in RED, GREEN and GOLD
 * alike — so changing it changes what a number is CALLED, never what it is
 * worth, and no value here is ever rescaled before it is formatted.
 */
export const SPARK_UNIT_MINOR = WIRE_PER_UNIT;

/**
 * Wire digits per displayed unit, i.e. log10(unitMinor).
 *
 * Only exact powers of ten are accepted; anything else falls back to the SPARK
 * divisor. A non-power-of-ten would mean the decimal point does not sit on a
 * digit boundary, and the whole point of the integer path below is that the
 * split is a string slice rather than a division. A zero or negative divisor
 * would render Infinity.
 */
function unitShift(unitMinor: number | undefined): number {
  if (unitMinor === undefined) return SPARK_MINOR_PRECISION;
  if (!Number.isFinite(unitMinor) || unitMinor < 1) return SPARK_MINOR_PRECISION;
  const shift = Math.round(Math.log10(unitMinor));
  if (shift < 0 || 10 ** shift !== unitMinor) return SPARK_MINOR_PRECISION;
  return shift;
}

function toMajor(minor: string | number | undefined | null, unitMinor: number): number | null {
  if (minor === undefined || minor === null || minor === "") return null;
  const n = typeof minor === "number" ? minor : Number(minor);
  if (!Number.isFinite(n)) return null;
  return n / unitMinor;
}

export interface FormatSparkOptions {
  /** BCP 47 locale used for digit grouping. */
  locale?: string;
  /** Decimal places (default 2). */
  dp?: number;
  /**
   * Wire minor units per displayed unit (default SPARK_UNIT_MINOR = 10 000).
   * The SM-443 contract passes each currency's `unitMinor`: GOLD keeps the
   * legacy SPARK lens, RED and GREEN use the epic's $1 currency unit (10 000).
   *
   * It moves the DECIMAL POINT, it does not rescale the amount. The value
   * handed to this function is always the original wire integer, so no digit
   * is lost at any dp — which is precisely what a `n / (unitMinor / 100)`
   * pre-division would do, since that produces a float and the integer path
   * below then truncates it (SM-484).
   */
  unitMinor?: number;
  /** Force a leading "+" on positive values (P&L deltas). */
  sign?: boolean;
  /** Drop a trailing ".00" for whole amounts. */
  trimZeros?: boolean;
  /** Rendered when the input is nullish/NaN (default "—"). */
  placeholder?: string;
}

/**
 * Format int64 minor units as a grouped SPARK number string (no unit
 * symbol), truncated to `dp`. Negative values get a real minus (U+2212).
 *   formatSpark("5900")          → "59.00"     (a 0.59 price → ⚡59)
 *   formatSpark("737500")        → "7,375.00"  (a balance → ⚡7,375)
 *   formatSpark("-5000")         → "−50.00"
 */
export function formatSpark(
  minor: string | number | undefined | null,
  opts: FormatSparkOptions = {},
): string {
  const {
    dp = SPARK_DISPLAY_DP,
    sign = false,
    trimZeros = false,
    placeholder = "—",
    locale,
    unitMinor,
  } = opts;
  const shift = unitShift(unitMinor);
  const major = toMajor(minor, 10 ** shift);
  if (major === null) return placeholder;
  const digits = truncatedDigits(minor as string | number, dp, trimZeros, shift);
  if (digits === null) return placeholder;
  const body =
    groupDigits(digits.whole, locale) +
    (digits.fraction ? decimalSeparator(locale) + digits.fraction : "");
  if (major < 0) return "−" + body;
  if (sign && major > 0) return "+" + body;
  return body;
}

// ── Why the digits are computed in integers, not floats ──────────────
//
// This used to be `Math.trunc(major * 10 ** dp) / 10 ** dp`, and it was
// wrong for roughly one wire value in fifteen. `1999 / 100` is
// 19.990000000000002 in binary floating point; multiplying back by 100
// gives 1998.9999999999998, and truncating that lands on 1998 — so a
// ⚡19.99 figure rendered as ⚡19.98. Same for wire 29 → 0.28, 57 →
// 0.56, 113 → 1.12, 123456789 → 1,234,567.88. Always one unit SHORT,
// never over, which is why it survived so long: it reads as
// conservative rounding rather than as a bug.
//
// Truncation is the deliberate rule (never show more than an account
// can spend), so it has to be exact. The wire value is an integer to
// begin with, so the whole calculation stays in integer space and the
// only float that survives is the sign test. Found and fixed in
// web-client during 06a; ported here so the three apps' digits cannot
// drift (SM-443 parity fixture, test/currency-parity.test.ts).

interface TruncatedDigits {
  /** Absolute integer part, ungrouped: "1234567". */
  whole: string;
  /** Fraction digits without a separator, already trimmed: "89" or "". */
  fraction: string;
}

function truncatedDigits(
  minor: string | number,
  dp: number,
  trimZeros: boolean,
  shift: number,
): TruncatedDigits | null {
  const raw = typeof minor === "number" ? String(Math.trunc(minor)) : String(minor).trim();
  const negative = raw.startsWith("-");
  const abs = (negative ? raw.slice(1) : raw).replace(/^\+/, "");
  // Non-integer input (a float that slipped through, scientific
  // notation) — fall back to the float path rather than mangling it.
  if (!/^\d+$/.test(abs)) {
    const n = Math.abs(Number(minor));
    if (!Number.isFinite(n)) return null;
    const f = 10 ** dp;
    const t = Math.trunc((n / 10 ** shift) * f) / f;
    const [w, fr = ""] = t.toFixed(dp).split(".");
    return { whole: w, fraction: trimZeros ? fr.replace(/0+$/, "") : fr };
  }
  // Digits of |minor| shifted so the decimal point sits `shift` digits
  // from the right — 2 for SPARK/GOLD (1 unit = 100 wire), 4 for the $1
  // currency unit RED and GREEN use (1 unit = 10 000 wire).
  const padded = abs.padStart(shift + 1, "0");
  const whole = padded.slice(0, padded.length - shift).replace(/^0+(?=\d)/, "");
  const available = padded.slice(padded.length - shift);
  const fractionAll = (available + "0".repeat(Math.max(0, dp - available.length))).slice(0, dp);
  return { whole, fraction: trimZeros ? fractionAll.replace(/0+$/, "") : fractionAll };
}

/** Group an ungrouped integer-digit string in `locale`. */
function groupDigits(whole: string, locale?: string): string {
  // A treasury-scale figure is allowed to exceed MAX_SAFE_INTEGER, and
  // those cannot round-trip through Number — group them by hand.
  const n = Number(whole);
  if (Number.isSafeInteger(n)) {
    try {
      return n.toLocaleString(locale, { useGrouping: true, maximumFractionDigits: 0 });
    } catch {
      return n.toLocaleString(undefined, { useGrouping: true, maximumFractionDigits: 0 });
    }
  }
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, separatorsOf(locale).group);
}

/** The active locale's decimal separator ("." / "," / "٫"). */
function decimalSeparator(locale?: string): string {
  return separatorsOf(locale).decimal;
}

function separatorsOf(locale?: string): { decimal: string; group: string } {
  try {
    const parts = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      useGrouping: true,
    }).formatToParts(1234.5);
    return {
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
      group: parts.find((p) => p.type === "group")?.value ?? ",",
    };
  } catch {
    return { decimal: ".", group: "," };
  }
}

/** Plain-text SPARK with the word unit, for hints / aria-labels: "12.34 SPARK". */
export function formatSparkText(
  minor: string | number | undefined | null,
  opts: FormatSparkOptions = {},
): string {
  const s = formatSpark(minor, opts);
  const ph = opts.placeholder ?? "—";
  return s === ph ? s : `${s} ${SPARK_LABEL}`;
}

/**
 * Inverse of formatSpark for outbound POSTs: a typed SPARK amount → wire minor
 * units. Since 1 SPARK = 1 cent, ⚡59 → "5900" and ⚡7375 → "737500".
 *   parseSparkToMinor("59")   → "5900"
 * Commas are tolerated (formatted input). Returns "0" for empty/NaN.
 */
export function parseSparkToMinor(
  input: string | number | undefined | null,
  precision = SPARK_MINOR_PRECISION,
): string {
  if (input === undefined || input === null || input === "") return "0";
  const raw = String(input).replace(/,/g, "").trim();
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(raw);
  if (!m) {
    // Scientific notation or junk. Fall back to the float path rather than
    // mangling it; a non-finite value reads as zero.
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n * 10 ** precision).toString() : "0";
  }
  const [, sgn, whole = "", frac = ""] = m;
  if (whole === "" && frac === "") return "0";
  // Digits of the typed amount with the point moved `precision` places right,
  // padding when fewer were typed and TRUNCATING when more were.
  const shifted = (whole + frac.padEnd(precision, "0").slice(0, precision)).replace(/^0+(?=\d)/, "");
  if (shifted === "" || /^0+$/.test(shifted)) return "0";
  return (sgn === "-" ? "-" : "") + shifted;
}

/**
 * Map the ledger's internal currency code to the operator-facing display
 * name. Everything in this system is SPARK; the wire code is "CREDIT".
 *   currencyLabel("CREDIT") → "SPARK"
 */
export function currencyLabel(code: string | undefined | null): string {
  if (!code) return SPARK_LABEL;
  return code === "CREDIT" ? SPARK_LABEL : code;
}

// ── SM-461: fractional-dollar price + money display ─────────────────
//
// Prices display on the unitless 0–1 scale per the SM-460 matrix: 1.00 =
// wire 10000 (precision 4). The retired SPARK-cents lens showed the same
// wire values ÷100; the wire integers are untouched (G2). A price is not
// money (SM-517), so it carries no "$" and no currency mark — money goes
// through <CurrencyAmount> with the currency's coin.

/** One dollar in wire minor units (10^price_precision at precision 4). */
export const WIRE_PER_DOLLAR = 10 ** SPARK_MINOR_PRECISION;

/**
 * Price display decimals for a market's tick: the fewest decimals that
 * render one tick exactly, floored at 2 and capped at the wire
 * precision 4 (SM-443 presentation contract). tick 100 → 2dp, 10 → 3dp,
 * 1 → 4dp; unknown/absent ticks read at 2dp.
 */
export function priceDpForTick(tickSize: string | number | undefined | null): number {
  const minor = typeof tickSize === "number" ? tickSize : Number(tickSize);
  if (!Number.isFinite(minor) || minor <= 0) return 2;
  let step = minor / WIRE_PER_DOLLAR;
  let dp = 0;
  while (dp < SPARK_MINOR_PRECISION && Math.abs(step - Math.round(step)) > 1e-9) {
    step *= 10;
    dp++;
  }
  return Math.max(2, dp);
}

/**
 * Format a wire price on the unitless 0–1 scale: formatPrice("5000") →
 * "0.50", formatPrice("10000") → "1.00" (the resolution value),
 * formatPrice("-5000") → "−0.50". dp follows the tick when given.
 *
 * Prices only — never money. Was formatPriceDollars and printed "$0.50";
 * renamed to match web-client (SM-986) so a "dollars" helper does not grow
 * back. Money is <CurrencyAmount> in the figure's own currency.
 */
export function formatPrice(
  minor: string | number | undefined | null,
  tickSize?: string | number,
  locale?: string,
): string {
  const n = typeof minor === "number" ? minor : Number(minor);
  if (minor === undefined || minor === null || minor === "" || !Number.isFinite(n)) return "—";
  const dp = priceDpForTick(tickSize);
  const price = n / WIRE_PER_DOLLAR;
  const body = Math.abs(price).toLocaleString(locale, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  return price < 0 ? `−${body}` : body;
}
